import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Pencil, Trash2, RefreshCw, Plus, Users, Loader2, Upload } from "lucide-react";
import { API, axios } from "@/utils/constants";

const emptyForm = {
  kennitala: "", name: "", address: "", postal_code: "", city: "",
  gsm: "", home_phone: "", work_phone: "", phone: "", email: "", comments: "",
};

export const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editCustomer, setEditCustomer] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [showNew, setShowNew] = useState(false);
  const [syncing, setSyncing] = useState(null);

  const fetchCustomers = async (q = "") => {
    try {
      const res = await axios.get(`${API}/customers`, { params: { q } });
      setCustomers(res.data);
    } catch { toast.error("Villa við að sækja viðskiptavini"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCustomers(); }, []);

  const handleSearch = (val) => {
    setSearch(val);
    fetchCustomers(val);
  };

  const handleSync = async () => {
    toast.loading("Samstilli viðskiptavini...", { id: "sync" });
    try {
      const res = await axios.post(`${API}/customers/sync`);
      toast.success(`${res.data.count} færslur samstilltar`, { id: "sync" });
      fetchCustomers(search);
    } catch { toast.error("Villa við samstillingu", { id: "sync" }); }
  };

  const handleSyncToPayday = async (e, c) => {
    e.stopPropagation();
    setSyncing(c.id);
    try {
      // Try to find existing customer in Payday
      const fetchRes = await axios.get(`${API}/payday/fetch-customer/${c.kennitala}`);
      let paydayId = null;
      if (fetchRes.data.success && fetchRes.data.customer) {
        paydayId = fetchRes.data.customer.id;
      } else {
        // Create in Payday
        const createRes = await axios.post(`${API}/payday/create-customer`, {
          kennitala: c.kennitala,
          name: c.name || "",
          email: c.email || "",
          phone: c.gsm || c.phone || "",
          address: c.address || "",
          zipCode: c.postal_code || "",
          city: c.city || "",
        });
        if (createRes.data.success) {
          paydayId = createRes.data.customer_id;
        } else {
          toast.error(createRes.data.message || "Villa við stofnun í Payday");
          return;
        }
      }
      // Update local record
      if (paydayId) {
        await axios.put(`${API}/customers/${c.id}`, { payday_customer_id: paydayId });
        toast.success(`${c.name} uppfærður í Payday`);
        fetchCustomers(search);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Villa við Payday tengingu");
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Eyða ${c.name}?`)) return;
    try {
      await axios.delete(`${API}/customers/${c.id}`);
      toast.success("Viðskiptavini eytt");
      fetchCustomers(search);
    } catch { toast.error("Villa við eyðingu"); }
  };

  const openEdit = (c) => {
    setFormData({
      kennitala: c.kennitala || "", name: c.name || "", address: c.address || "",
      postal_code: c.postal_code || "", city: c.city || "",
      gsm: c.gsm || "", home_phone: c.home_phone || "", work_phone: c.work_phone || "",
      phone: c.phone || "", email: c.email || "", comments: c.comments || "",
    });
    setEditCustomer(c);
  };

  const openNew = () => { setFormData(emptyForm); setShowNew(true); };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/customers/${editCustomer.id}`, formData);
      toast.success("Viðskiptavinur uppfærður");
      setEditCustomer(null);
      fetchCustomers(search);
    } catch { toast.error("Villa við vistun"); }
  };

  const handleCreate = async () => {
    if (!formData.kennitala || !formData.name) {
      toast.error("Kennitala og nafn eru nauðsynleg"); return;
    }
    try {
      await axios.post(`${API}/customers`, formData);
      toast.success("Viðskiptavinur skráður");
      setShowNew(false);
      fetchCustomers(search);
    } catch (e) { toast.error(e.response?.data?.detail || "Villa við skráningu"); }
  };

  const sorted = useMemo(() =>
    [...customers].sort((a, b) => (a.name || "").localeCompare(b.name || "", "is")),
    [customers]);

  const loanCount = useMemo(() =>
    customers.reduce((s, c) => s + (c.related_loans?.length || 0), 0), [customers]);

  const set = (field, val) => setFormData(prev => ({ ...prev, [field]: val }));

  const renderForm = (onSave, saveLabel) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Kennitala</Label>
          <Input value={formData.kennitala} onChange={e => set("kennitala", e.target.value)}
            className="h-8 text-sm" disabled={!!editCustomer} data-testid="customer-kennitala" />
        </div>
        <div>
          <Label className="text-xs">Nafn</Label>
          <Input value={formData.name} onChange={e => set("name", e.target.value)}
            className="h-8 text-sm" data-testid="customer-name" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-xs">Heimilisfang</Label>
          <Input value={formData.address} onChange={e => set("address", e.target.value)}
            className="h-8 text-sm" data-testid="customer-address" />
        </div>
        <div>
          <Label className="text-xs">Póstnúmer</Label>
          <Input value={formData.postal_code} onChange={e => set("postal_code", e.target.value)}
            className="h-8 text-sm" data-testid="customer-postal-code" />
        </div>
        <div>
          <Label className="text-xs">Borg</Label>
          <Input value={formData.city} onChange={e => set("city", e.target.value)}
            className="h-8 text-sm" data-testid="customer-city" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <div>
          <Label className="text-xs">GSM</Label>
          <Input value={formData.gsm} onChange={e => set("gsm", e.target.value)}
            className="h-8 text-sm" data-testid="customer-gsm" />
        </div>
        <div>
          <Label className="text-xs">Heimasími</Label>
          <Input value={formData.home_phone} onChange={e => set("home_phone", e.target.value)}
            className="h-8 text-sm" data-testid="customer-home-phone" />
        </div>
        <div>
          <Label className="text-xs">Vinnusími</Label>
          <Input value={formData.work_phone} onChange={e => set("work_phone", e.target.value)}
            className="h-8 text-sm" data-testid="customer-work-phone" />
        </div>
        <div>
          <Label className="text-xs">Netfang</Label>
          <Input value={formData.email} onChange={e => set("email", e.target.value)}
            className="h-8 text-sm" data-testid="customer-email" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Athugasemdir</Label>
        <Textarea value={formData.comments} onChange={e => set("comments", e.target.value)}
          className="text-sm min-h-[60px]" data-testid="customer-comments" />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" className="h-6 text-[10px] px-2"
          onClick={() => { setEditCustomer(null); setShowNew(false); }}>Hætta við</Button>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2"
          onClick={onSave} data-testid="customer-save-btn">{saveLabel}</Button>
      </div>
    </div>
  );

  if (loading) return (
    <div className="p-8 text-sm text-gray-500 flex items-center gap-2" data-testid="customers-loading">
      <Loader2 className="h-4 w-4 animate-spin" />Hleður viðskiptavinum...
    </div>
  );

  return (
    <div className="p-8" data-testid="customers-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Viðskiptamenn</h1>
          <div className="flex gap-2">
            <Button onClick={handleSync} className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2" data-testid="sync-customers-btn">
              <RefreshCw className="h-3 w-3 mr-1" />Samstilla
            </Button>
            <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 h-6 text-[10px] px-2" data-testid="new-customer-btn">
              <Plus className="h-3 w-3 mr-1" />Skrá
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 flex gap-4 items-center">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input value={search} onChange={e => handleSearch(e.target.value)}
              placeholder="Leita eftir nafni, kennitölu, netfangi..."
              className="pl-10" data-testid="customer-search" />
          </div>
        </div>

        {/* Summary counts */}
        <div className="flex items-center gap-4 mb-3 text-[10px] text-gray-500" data-testid="customer-summary-counts">
          <span className="text-blue-600">Samtals <span className="font-semibold">{sorted.length}</span></span>
          <span className="text-green-600">Tengd lán <span className="font-semibold">{loanCount}</span></span>
        </div>

        {/* List */}
        {sorted.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-14 text-center" data-testid="customers-empty-state">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Users className="h-5 w-5 text-gray-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Engir viðskiptavinir fundust</h2>
            <p className="mt-2 text-sm text-gray-500">Prófaðu að breyta leit eða smelltu á Samstilla.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5" data-testid="customers-list">
            {sorted.map((c) => (
              <div
                key={c.id}
                onClick={() => openEdit(c)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 flex items-center gap-3 cursor-pointer hover:border-blue-200 hover:bg-blue-50/30"
                data-testid={`customer-row-${c.id}`}
              >
                {/* Badge area */}
                <div className="shrink-0 w-14">
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-[10px] px-2 py-0.5">
                    {(c.related_loans?.length || 0)} lán
                  </Badge>
                </div>

                {/* Name + contact */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-xs truncate">{c.name}</span>
                    <span className="text-[9px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 shrink-0">{c.kennitala}</span>
                  </div>
                  <div className="text-[10px] text-gray-400 truncate">
                    {c.address}{c.city ? `, ${c.city}` : ""}
                  </div>
                </div>

                {/* Phone + email columns */}
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-[10px] text-gray-500 w-[5.5rem] text-left tabular-nums">{c.gsm || "—"}</span>
                  <span className="text-[10px] text-gray-400 w-[5.5rem] text-left tabular-nums">{c.home_phone || "—"}</span>
                  <span className="text-[10px] text-blue-600 w-[12rem] text-left truncate">
                    {c.email ? <a href={`mailto:${c.email}`} className="hover:underline" onClick={e => e.stopPropagation()}>{c.email}</a> : "—"}
                  </span>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-0.5 shrink-0 border-l pl-2" onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" onClick={(e) => handleSyncToPayday(e, c)}
                    disabled={syncing === c.id}
                    className="p-1 h-6 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-[10px] font-medium gap-1" title="Uppfæra í Payday"
                    data-testid={`sync-payday-${c.id}`}>
                    {syncing === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    Uppfæra
                  </Button>
                  <Button variant="ghost" onClick={() => openEdit(c)}
                    className="p-1 h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Breyta"
                    data-testid={`edit-customer-${c.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" onClick={(e) => handleDelete(e, c)}
                    className="p-1 h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50" title="Eyða"
                    data-testid={`delete-customer-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editCustomer} onOpenChange={() => setEditCustomer(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-sm">Viðskiptamaður — {editCustomer?.name}</DialogTitle>
          </DialogHeader>
          {renderForm(handleSave, "Vista")}
        </DialogContent>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-sm">Nýr viðskiptamaður</DialogTitle>
          </DialogHeader>
          {renderForm(handleCreate, "Skrá")}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
